
import {
  RoomsApi,
  LADSApi,
  EcoApi,
  AutomationApi,
  IncidentsApi,
  Configuration,
  HistoryApi,
  UAFunction,
  AggregateFunctionEnum,
  type RoomList,
  type RoomResponse,
  type RoomMetaInput,
  type EcoStats,
  type EcoRecommendations,
  type Automation,
  type AutomationInput,
  type AutomationList,
  type Incident,
  type IncidentQueryResponse,
  type IncidentHistoryResponse,
  type UALADSDevice,
  type UALADSFunctionalUnit,
  type HistoryReadResponse,
  type AggregateReadResponse, ValueOverrideInputReferenceTypeEnum, type ValueOverride,
  type MethodInfo,
  type ControlCallResult,
  type SerializedVariant,
} from '../../server/lads-api/clients/lcc-openapi';
import { APiError } from "./common.ts";

const LCC_BASE_PATH = import.meta.env.VITE_LCC_BASE_PATH;
const LADS_BASE_PATH = import.meta.env.VITE_LADS_BASE_PATH;

// Helper function to create API clients with authentication
// Supports both simple token string and function-based token with scopes
type AccessTokenType = string | ((name?: string, scopes?: string[]) => string | Promise<string>);

// Generic helper to create any API client with shared configuration
function createClient<T extends RoomsApi | LADSApi | EcoApi | AutomationApi | IncidentsApi | HistoryApi>(
  ClientClass: new (config: Configuration) => T,
  basePath: string,
  accessToken?: AccessTokenType
): T {
  if (!basePath) {
    throw new Error("API Client BASE_PATH is not defined")
  }

  // Wrap accessToken to include "Bearer " prefix
  let wrappedAccessToken: AccessTokenType | undefined;
  if (accessToken) {
    if (typeof accessToken === 'string') {
      // If it's a string, wrap it in a function that returns "Bearer <token>"
      wrappedAccessToken = () => {
        return `Bearer ${accessToken}`;
      };
    } else {
      // If it's already a function, wrap it to add the Bearer prefix
      wrappedAccessToken = async (name?: string, scopes?: string[]) => {
        const token = await accessToken(name, scopes);
        return token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      };
    }
  } else {
    console.warn("[createClient] No accessToken provided!");
  }

  const config = new Configuration({
    basePath: basePath,
    ...(wrappedAccessToken && {
      accessToken: wrappedAccessToken,
    }),
  });
  return new ClientClass(config);
}

// Function to get all devices or filtered devices
export const receiveAllLADSDevices = async (filter?: string, accessToken?: string): Promise<UALADSDevice[]> => {


  try {
    const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);
    if (!ladsClient) {
      return Promise.reject(new Error("LADS_BASE_PATH is not defined"));
    }

    return await ladsClient.getDevices({
      hierarchicalLocation: filter || undefined,
    });
  } catch (error) {
    console.error("[receiveAllLADSDevices] Error:", error);
    throw APiError.fromError(error);
  }
};

// Function to get a specific device by ID
export const receiveLADSDevice = async (deviceId: string, accessToken?: string): Promise<UALADSDevice> => {

  try {
    const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);


    return await ladsClient.getDevice({
      deviceId,
    });

  } catch (error) {
    console.error("[receiveLADSDevice] Error:", error);
    if (error instanceof Error) {
      console.error("[receiveLADSDevice] Message:", error.message);
      console.error("[receiveLADSDevice] Stack:", error.stack);
    }
    throw APiError.fromError(error);
  }
};

// Fetch a binary OPC UA variable (e.g. a DeviceTypeImage file) as a Blob.
// Goes through the authenticated client so the oauth2-protected endpoint gets
// the Bearer token — a plain <img src> could not.
export const fetchLADSBinary = async (nodeId: string, accessToken?: string): Promise<Blob> => {
  try {
    const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);
    return await ladsClient.getBinary({ nodeId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Function to get all functional units of a device
export const receiveLADSFunctionalUnits = async (deviceId: string, accessToken?: string): Promise<UALADSFunctionalUnit[]> => {
  try {
    const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);

    const rawUnits = await ladsClient.getFunctionalUnits({
      deviceId,
    });

    // Handle empty or null response - return empty array as default
    return rawUnits || [];
  } catch (error) {
    console.error("[receiveLADSFunctionalUnits] Error:", error);
    if (error instanceof Error) {
      console.error("[receiveLADSFunctionalUnits] Message:", error.message);
    }
    throw APiError.fromError(error);
  }
};

// Function to get a specific functional unit of a device
export const receiveLADSFunctionalUnit = async (deviceId: string, functionalUnitId: string, accessToken?: string): Promise<UALADSFunctionalUnit> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);

  try {
    return await ladsClient.getFunctionalUnit({ deviceId, functionalUnitId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Direct browser export (no panel/USB): stream the whole-device XLSX history
// workbook straight from the LADS proxy (GET /lads/history/{deviceId}/export).
// The panel/USB path goes through the middleware instead (see exportChannelData).
export const exportDeviceHistory = async (
  deviceId: string,
  startTime: Date,
  endTime: Date,
  accessToken?: string
): Promise<{ blob: Blob; filename: string | null }> => {
  // HistoryApi läuft über LCC_BASE_PATH (wie readHistoryValue/readAggregateValue) —
  // das löst im Traefik-Deployment korrekt zu /api/v2/lads/... auf. LADS_BASE_PATH
  // landete ohne /api/v2-Präfix beim webapp-Catch-all → index.html statt XLSX.
  const historyClient = createClient(HistoryApi, LCC_BASE_PATH, accessToken);

  try {
    // Raw response so we can read the backend's Content-Disposition, which
    // carries the "<name>-<serial>-<date>.xlsx" filename (single source of
    // truth — the panel/USB branch reads the same header via the middleware).
    const response = await historyClient.exportValuesRaw({
      deviceId,
      startTime,
      endTime,
    });
    const filename = filenameFromDisposition(
      response.raw.headers.get('content-disposition')
    );
    return { blob: await response.value(), filename };
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Panel builds set this to the middleware base (e.g. ".../middleware") so the
// export can be written to a USB drive. Web builds leave it empty → the export
// streams straight from the LADS proxy as a browser download (no middleware).
const USB_EXPORT_PATH = import.meta.env.VITE_USB_EXPORT_PATH;

export type ExportResult = {
  status: number;
  // 200 browser-download branch: the XLSX file (LADS direct, or middleware stream).
  blob?: Blob;
  filename?: string;
  // 201/404/405 USB branch: JSON status envelope from the middleware.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
};

const filenameFromDisposition = (disposition: string | null): string | null => {
  if (!disposition) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : null;
};

// Export a room's channels as an XLSX workbook. Web build → direct LADS
// download. Panel build (VITE_USB_EXPORT_PATH set) → middleware, which can
// write to a USB drive and returns a 201/404/405 USB status envelope.
export const exportChannelData = async (
  token: string,
  channelIds: string[],
  collectionId: string,
  startDate: Date,
  endDate: Date
): Promise<ExportResult> => {
  const deviceId = String(channelIds[0] ?? '').split('/')[0];
  if (!deviceId) throw new Error('channelIds missing — cannot derive deviceId for export');

  // Web build (no panel/USB): stream the whole-device XLSX as a browser download.
  if (!USB_EXPORT_PATH) {
    const { blob, filename } = await exportDeviceHistory(deviceId, startDate, endDate, token);
    return { status: 200, blob, filename: filename ?? `${deviceId}.xlsx` };
  }

  // Panel build: route through the middleware so it can write to a USB drive.
  const response = await fetch(`${USB_EXPORT_PATH}/export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ channelIds, collectionId, startDate, endDate }),
  });

  // 200 = middleware streamed the XLSX directly. 201/404/405 = USB status envelope.
  if (response.status === 200) {
    return {
      status: 200,
      blob: await response.blob(),
      filename:
        filenameFromDisposition(response.headers.get('content-disposition')) ??
        'export.xlsx',
    };
  }

  const data = await response.json().catch(() => null);
  return { status: response.status, data };
};

// Function to get function values of a specific functional unit
export const receiveLADSFunctionSet = async (deviceId: string, functionalUnitId: string, accessToken?: string): Promise<UAFunction[]> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);

  try {
    return await ladsClient.getFunctions({ deviceId, functionalUnitId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const receiveAllLADSRooms = async (accessToken?: string): Promise<RoomList> => {
  const lccClient = createClient(RoomsApi, LCC_BASE_PATH, accessToken);

  try {
    console.log("rooms", lccClient.getRooms());
    return await lccClient.getRooms();
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const updateRoomMeta = async (
  roomId: string,
  roomMetaInput: RoomMetaInput,
  accessToken?: string
): Promise<RoomResponse> => {
  const lccClient = createClient(RoomsApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.updateRoomMeta({ roomId, roomMetaInput });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const receiveLADSRoom = async (roomId: string, accessToken?: string): Promise<RoomResponse> => {
  const lccClient = createClient(RoomsApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.getRoom({ roomId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Eco endpoints
export const receiveLADSEcoStats = async (roomId: string, accessToken?: string): Promise<EcoStats> => {
  const lccClient = createClient(EcoApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.getRoomStats({ roomId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const receiveLADSEcoRecommendations = async (roomId: string, accessToken?: string): Promise<EcoRecommendations> => {
  const lccClient = createClient(EcoApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.getRoomRecommendations({ roomId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Automation endpoints
export const receiveAllLADSAutomations = async (room?: string, device?: string[], accessToken?: string): Promise<AutomationList> => {
  const lccClient = createClient(AutomationApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.getAutomations({ room, device });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const receiveLADSAutomation = async (automationId: string, accessToken?: string): Promise<Automation> => {
  const lccClient = createClient(AutomationApi, LCC_BASE_PATH, accessToken);

  try {
    const list = await lccClient.getAutomations({});
    const automation = list.data?.find((a: Automation) => a.id === automationId);
    if (!automation) {
      throw new Error(`Automation with id ${automationId} not found`);
    }
    return automation;
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const createLADSAutomation = async (automation: AutomationInput, accessToken?: string): Promise<Automation> => {
  const lccClient = createClient(AutomationApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.createAutomation({ automationInput: automation });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const updateLADSAutomation = async (automationId: string, automation: AutomationInput, accessToken?: string): Promise<Automation> => {
  const lccClient = createClient(AutomationApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.updateAutomation({ automationId, automationInput: automation });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const deleteLADSAutomation = async (automationId: string, accessToken?: string): Promise<void> => {
  const lccClient = createClient(AutomationApi, LCC_BASE_PATH, accessToken);

  try {
    await lccClient.deleteAutomation({ automationId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Incidents endpoints
export const receiveAllLADSIncidents = async (
  roomId?: string,
  context?: string,
  severity?: "NOTICE" | "INFO" | "WARNING" | "ALERT" | "ERROR",
  active?: boolean,
  status?: "NEW" | "RESOLVED" | "CONFIRMED" | "REPORTED" | "CLOSED",
  limit?: number,
  offset?: number,
  accessToken?: string,
  lifecycle?: "open" | "resolved"
): Promise<IncidentQueryResponse> => {
  const lccClient = createClient(IncidentsApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.getIncidents({
      room: roomId,
      context,
      severity,
      active,
      status,
      lifecycle,
      limit,
      offset,
    });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const receiveLADSIncident = async (incidentId: string, accessToken?: string): Promise<Incident> => {
  const lccClient = createClient(IncidentsApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.getIncident({ incidentId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const receiveLADSIncidentHistory = async (incidentId: string, accessToken?: string): Promise<IncidentHistoryResponse> => {
  const lccClient = createClient(IncidentsApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.getIncidentHistory({ incidentId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Acknowledge an active incident — equivalent to "Gemeldet markieren". The
// backend raises an AuditConditionAcknowledgeEventType on the OPC-UA source
// node; the bubbled event lands in lads_alarm_events, and IncidentsService
// folds the new history into status=REPORTED on the next read.
export const acknowledgeLADSAlarm = async (
  incidentId: string,
  comment?: string,
  accessToken?: string
): Promise<void> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);
  try {
    await ladsClient.ackAlarm({ incidentId, ackAlarmRequest: comment ? { comment } : undefined });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Confirm an acknowledged incident. Raises AuditConditionConfirmEventType,
// which folds to status=CONFIRMED in the IncidentsService view.
export const confirmLADSAlarm = async (
  incidentId: string,
  comment?: string,
  accessToken?: string
): Promise<void> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);
  try {
    await ladsClient.confirmAlarm({ incidentId, confirmAlarmRequest: comment ? { comment } : undefined });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Report an incident — escalation/notification distinct from Ack/Confirm.
// Raises AuditConditionReportedEventType (Essentim companion spec,
// ns=…;i=15001) on the alarm's source node. Folds to status=REPORTED.
export const reportLADSAlarm = async (
  incidentId: string,
  comment?: string,
  accessToken?: string
): Promise<void> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);
  try {
    await ladsClient.reportAlarm({ incidentId, reportAlarmRequest: comment ? { comment } : undefined });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// History API types
export type HistoryDataValue = {
  // Der generierte OpenAPI-Client liefert Timestamps als Date (FromJSON →
  // new Date(...)); ältere Pfade reichen sie als ISO-String durch. Beides
  // zulassen — Consumer wrappen ohnehin in new Date().
  serverTimestamp: string | Date;
  sourceTimestamp?: string | Date;
  statusCode: string;
  value: number | string | boolean | null;
};

/** Historical data for a single value variable */
export type HistoryValueData = {
  /** OPC UA browse path (e.g., "TemperatureController/CurrentValue") */
  browsePath: string;
  /** OPC UA status code (e.g., "Good", "BadHistoryOperationUnsupported") */
  statusCode: string;
  /** Historical data points */
  historyData: HistoryDataValue[];
  /** Continuation point for pagination (null if no more data) */
  continuationPoint?: string | null;
  /** Engineering units (e.g., "°C") */
  engineeringUnits?: string;
  /** Value precision for rounding */
  valuePrecision?: number;
  /** Engineering unit range (Grenzen optional — Aggregat-Response kann sie leer lassen) */
  euRange?: { low?: number; high?: number };
  /** For discrete types: array of state names indexed by numeric value */
  enumStrings?: string[];
};


/**
 * Fetch historical data for a LADS function
 * @param deviceId - Device browseName
 * @param functionalUnitId - FunctionalUnit browseName
 * @param functionId - Function browseName
 * @param startTime - Start of time range (ISO 8601)
 * @param endTime - End of time range (ISO 8601)
 * @param numValuesPerNode - Max number of values to return (default: 1000)
 * @param accessToken - Auth token
 */
export const receiveLADSHistoryValues = async (
  deviceId: string,
  functionalUnitId: string,
  functionId: string,
  startTime: Date,
  endTime: Date,
  numValuesPerNode: number = 1000,
  accessToken?: string
): Promise<HistoryReadResponse> => {

  const lccClient = createClient(HistoryApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.readHistoryValue({ deviceId, functionalUnitId, functionId, endTime, startTime, numValuesPerNode });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

/**
 * Aggregate function types supported by the API
 */
//export type AggregateFunction = "Average" | "Minimum" | "Maximum" | "Count" | "Total";



/**
 * Fetch aggregated historical values from LADS History API
 * Uses TimescaleDB time_bucket for efficient aggregation
 *
 * @param deviceId - Device browse name
 * @param functionalUnitId - Functional unit browse name
 * @param functionId - Function browse name
 * @param startTime - Start of time range
 * @param endTime - End of time range
 * @param aggregateFunction - Aggregate function (Average, Minimum, Maximum, Count, Total)
 * @param processingInterval - Bucket size in milliseconds (e.g., 600000 = 10 min)
 * @param accessToken - Auth token
 */
export const receiveLADSAggregateValues = async (
  deviceId: string,
  functionalUnitId: string,
  functionId: string,
  startTime: Date,
  endTime: Date,
  aggregateFunction: AggregateFunctionEnum,
  processingInterval: number = 600000, // 10 minutes default
  variablePath?: string, // Optional: "currentValue", "targetValue", "sensorValue"
  accessToken?: string
): Promise<AggregateReadResponse> => {
  const lccClient = createClient(HistoryApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.readAggregateValue({ deviceId, functionalUnitId, functionId, endTime, startTime, aggregateFunction, variablePath, processingInterval });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// Update device properties (componentName, hierarchicalLocation, etc.)
// Uses the generic PUT /lads/override endpoint per field.
export type UpdateLADSDeviceInput = {
  assetId?: string;
  componentName?: string;
  hierarchicalLocation?: string;
  operationalLocation?: string;
};

export const updateDeviceName = async (
  deviceId: string,
  property: string,
  name: string,
  accessToken?: string
): Promise<ValueOverride> => {
  const lccClient = createClient(LADSApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.setOverride({
      valueOverrideInput: {
        browsePath: `DeviceSet/${deviceId}/${property}`,
        value: name,
        dataType: "String",
        referenceType: ValueOverrideInputReferenceTypeEnum.Variable
      },
    });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

/**
 * Write a control-function TargetValue through to the real device.
 *
 * Hits the hand-written POST /lads/control-write endpoint (not part of the
 * generated client). Unlike setOverride this is a genuine write-through: the
 * proxy forwards the value to the source device and the new value comes back via
 * the normal value sync. `functionPath` is the function node path
 * (DeviceSet/{deviceId}/FunctionalUnitSet/{unit}/FunctionSet/{func}); the
 * writable leaf is its TargetValue child.
 */
export const setControlValue = async (
  functionPath: string,
  value: number | string | boolean,
  accessToken?: string
): Promise<void> => {
  const browsePath = `${functionPath}/TargetValue`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const response = await fetch(`${LCC_BASE_PATH}/lads/control-write`, {
    method: "POST",
    headers,
    body: JSON.stringify({ browsePath, value }),
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(`control-write failed (${response.status}): ${detail}`);
  }
};

/** List a device's reconstructed, callable methods with argument metadata. */
export const receiveDeviceMethods = async (
  deviceId: string,
  accessToken?: string
): Promise<MethodInfo[]> => {
  const lccClient = createClient(LADSApi, LCC_BASE_PATH, accessToken);
  try {
    return await lccClient.getDeviceMethods({ deviceId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

/** Call a reconstructed method through to the device; returns its output args. */
export const callDeviceMethod = async (
  browsePath: string,
  inputArguments: SerializedVariant[],
  accessToken?: string
): Promise<ControlCallResult> => {
  const lccClient = createClient(LADSApi, LCC_BASE_PATH, accessToken);
  try {
    return await lccClient.controlCall({ controlCallInput: { browsePath, inputArguments } });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

export const updatePosition = async (
  deviceId: string,
  position: string,
  accessToken?: string
): Promise<ValueOverride> => {
  const lccClient = createClient(LADSApi, LCC_BASE_PATH, accessToken);

  try {
    return await lccClient.setOverride({ valueOverrideInput: {
      browsePath: `DeviceSet/${deviceId}/HierarchicalLocation`,
      value: position,
      dataType: "String",
      referenceType: ValueOverrideInputReferenceTypeEnum.Property
    }});
  } catch (error) {
    throw APiError.fromError(error);
  }
};

// ============================================================================
// Function Overrides API (Alarm Thresholds)
// ============================================================================

/**
 * Local overrides for function alarm thresholds.
 * These override the OPC UA AlarmMonitor values.
 */
export type FunctionOverrides = {
  /** Upper critical limit (HighHighLimit) */
  highHighLimit?: number | null;
  /** Upper warning limit (HighLimit) */
  highLimit?: number | null;
  /** Lower warning limit (LowLimit) */
  lowLimit?: number | null;
  /** Lower critical limit (LowLowLimit) */
  lowLowLimit?: number | null;
  /** Seconds the condition must hold before alarm fires (matches OpenAPI) */
  onDelay?: number;
  /** Seconds the condition must clear before alarm clears */
  offDelay?: number;
  /** Whether the AlarmMonitor is active. We send true on save so a fresh
   *  threshold doesn't land on a still-disabled monitor. */
  enabledState?: boolean;
};

/**
 * Get a single function (includes alarm thresholds)
 */
export const receiveLADSFunction = async (
  deviceId: string,
  functionalUnitId: string,
  functionId: string,
  accessToken?: string
): Promise<UAFunction> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);

  try {
    return await ladsClient.getFunction({ deviceId, functionalUnitId, functionId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

/**
 * Get function overrides (alarm thresholds)
 */
export const receiveLADSFunctionOverrides = async (
  deviceId: string,
  functionalUnitId: string,
  functionId: string,
  accessToken?: string
): Promise<FunctionOverrides> => {
  try {
    const func = await receiveLADSFunction(deviceId, functionalUnitId, functionId, accessToken);
    return func as unknown as FunctionOverrides;
  } catch (error) {
    throw APiError.fromError(error);
  }
};

/**
 * Update function overrides (alarm thresholds)
 */
export const updateLADSFunctionOverrides = async (
  deviceId: string,
  functionalUnitId: string,
  functionId: string,
  overrides: Partial<FunctionOverrides>,
  accessToken?: string
): Promise<FunctionOverrides> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);

  try {
    // Backend accepts `null` for "clear this limit" but the generated
    // AlarmMonitorSettings type only allows `number | undefined`. Cast to
    // bypass — the OpenAPI spec is just under-specified on nullability.
    const result = await ladsClient.updateAlarmMonitor({
      deviceId,
      functionalUnitId,
      functionId,
      alarmMonitorSettings: overrides as never,
    });
    return result as unknown as FunctionOverrides;
  } catch (error) {
    throw APiError.fromError(error);
  }
};

/**
 * Delete function overrides (remove all user-defined limits)
 */
export const deleteLADSFunctionOverrides = async (
  deviceId: string,
  functionalUnitId: string,
  functionId: string,
  accessToken?: string
): Promise<void> => {
  const ladsClient = createClient(LADSApi, LADS_BASE_PATH, accessToken);

  try {
    await ladsClient.deleteAlarmMonitor({ deviceId, functionalUnitId, functionId });
  } catch (error) {
    throw APiError.fromError(error);
  }
};

